import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { ExternalLink, MapPin, Calendar, Ruler } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Rubber band (efeito borracha) — parecido com iOS.
 */
function rubberBand(dx, dimension, constant = 0.55) {
  if (dimension <= 0) return dx;
  const abs = Math.abs(dx);
  const sign = Math.sign(dx);
  const result = (dimension * constant * abs) / (dimension + constant * abs);
  return sign * result;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(n, max));
}

/**
 * Trunca sem scroll (estilo iOS), sem depender de plugin.
 */
function TruncatedText({ children, lines = 4, className = "" }) {
  return (
    <div
      className={className}
      style={{
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}

/**
 * Detecta mobile por breakpoint (default: <1024px)
 */
function useIsMobile(breakpoint = 1024) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = () => setIsMobile(mq.matches);

    onChange();

    // Safari antigo não suporta addEventListener em mediaquery
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [breakpoint]);

  return isMobile;
}

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState("todos");
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal + galeria
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Auto-play opcional
  const [autoPlayOn, setAutoPlayOn] = useState(false);

  const categories = useMemo(
    () => [
      { id: "todos", name: "TODOS OS PROJETOS" },
      { id: "feiras-comerciais", name: "FEIRAS COMERCIAIS" },
      { id: "exposicoes", name: "EXPOSIÇÕES" },
      { id: "eventos", name: "EVENTOS CORPORATIVOS" },
      { id: "quiosques", name: "QUIOSQUES & PDVS" },
      { id: "cenografias", name: "CENOGRAFIAS" },
      { id: "stands-modulares", name: "STANDS MODULARES" },
    ],
    []
  );

  // ===== Fetch =====
  useEffect(() => {
    let alive = true;

    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projetos`);
        if (!alive) return;
        setProjects(response.data);
        setError(null);
      } catch (err) {
        if (!alive) return;
        setError("Erro ao carregar projetos");
        const { projectsData } = await import("../data/mockData");
        setProjects(projectsData);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    fetchProjects();
    return () => {
      alive = false;
    };
  }, []);

  const filteredProjects =
    activeFilter === "todos"
      ? projects
      : projects.filter((p) => p.category === activeFilter);

  // ===== Helpers =====
  const getProjectImages = useCallback((project) => {
    if (!project) return [];
    const arr =
      Array.isArray(project.images) && project.images.length > 0
        ? project.images
        : [project.image].filter(Boolean);

    const clean = arr.filter(Boolean);
    return Array.from(new Set(clean));
  }, []);

  const openProject = useCallback((project) => {
    setSelectedProject(project);
    setCurrentIndex(0);
    setIsFullscreen(false);
  }, []);

  const closeProject = useCallback(() => {
    setSelectedProject(null);
    setIsFullscreen(false);
  }, []);

  // ===== Loading =====
  if (loading) {
    return (
      <section id="portfolio" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF1616]" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="portfolio" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight">
            NOSSO <span className="text-[#FF1616]">PORTFÓLIO</span>
          </h2>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto font-medium">
            Conheça alguns projetos que transformaram ideias em experiências
            extraordinárias
          </p>

          {/* Aviso: Em Manutenção */}
          <div className="flex justify-center mt-10">
            <div className="relative inline-flex items-center gap-2 rounded-full bg-amber-200/70 text-amber-950 px-5 py-2 text-sm font-black shadow-lg ring-1 ring-amber-300/60 backdrop-blur">
              <span className="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-amber-500 animate-ping opacity-60" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
              <span aria-hidden="true" className="text-[14px] leading-none">
                ⚠️
              </span>
              <span className="tracking-wide">EM MANUTENÇÃO</span>
            </div>
          </div>

          {error && (
            <p className="mt-4 text-sm text-red-600 font-medium">{error}</p>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={activeFilter === category.id ? "default" : "outline"}
              onClick={() => setActiveFilter(category.id)}
              className={`px-6 py-2 transition-all duration-300 font-bold tracking-wide text-sm ${
                activeFilter === category.id
                  ? "bg-[#FF1616] text-white border-0 shadow-lg hover:bg-red-700"
                  : "text-gray-700 border-gray-300 hover:border-[#FF1616] hover:text-[#FF1616]"
              }`}
              type="button"
            >
              {category.name}
            </Button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <Card
              key={project.id}
              className="group hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden border-0 bg-white"
              onClick={() => openProject(project)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") openProject(project);
              }}
            >
              <div className="relative overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <Badge className="absolute top-4 left-4 bg-[#FF1616] text-white hover:bg-red-700 font-bold tracking-wide">
                  {project.categoryName || project.category_name}
                </Badge>

                {/* ✅ agora o botão também abre o modal */}
                <Button
                  size="sm"
                  className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 bg-white text-gray-800 hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    openProject(project);
                  }}
                  type="button"
                  aria-label="Abrir projeto"
                  title="Abrir"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>

              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#FF1616] transition-colors tracking-wide">
                  {project.title}
                </h3>

                <p className="text-gray-600 mb-4 leading-relaxed">
                  {project.description}
                </p>

                <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
                  <div className="flex items-center">
                    <Ruler className="w-4 h-4 mr-2 text-[#FF1616]" />
                    {project.area}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-[#FF1616]" />
                    {project.year}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

 {/* Aviso: Em Manutenção */}
          <div className="flex justify-center mt-10">
            <div className="relative inline-flex items-center gap-2 rounded-full bg-amber-200/70 text-amber-950 px-5 py-2 text-sm font-black shadow-lg ring-1 ring-amber-300/60 backdrop-blur">
              <span className="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-amber-500 animate-ping opacity-60" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
              <span aria-hidden="true" className="text-[14px] leading-none">
                ⚠️
              </span>
              <span className="tracking-wide">EM MANUTENÇÃO</span>
            </div>
          </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Button
            size="lg"
            className="bg-[#FF1616] hover:bg-red-700 text-white px-8 py-4 font-bold tracking-wide"
            onClick={() => setActiveFilter("todos")}
            type="button"
          >
            VER TODOS OS PROJETOS
          </Button>
        </div>
      </div>

      {/* ===================== MODAL ===================== */}
      {selectedProject && (
        <PortfolioModalLikePrint
          selectedProject={selectedProject}
          closeProject={closeProject}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
          autoPlayOn={autoPlayOn}
          setAutoPlayOn={setAutoPlayOn}
          getProjectImages={getProjectImages}
        />
      )}
    </section>
  );
};

function PortfolioModalLikePrint({
  selectedProject,
  closeProject,
  currentIndex,
  setCurrentIndex,
  isFullscreen,
  setIsFullscreen,
  autoPlayOn,
  setAutoPlayOn,
  getProjectImages,
}) {
  const images = useMemo(
    () => getProjectImages(selectedProject),
    [selectedProject, getProjectImages]
  );

  const isMobile = useIsMobile(1024);

  // ✅ garante: mobile SEM fullscreen e SEM autoplay
  useEffect(() => {
    if (isMobile && isFullscreen) setIsFullscreen(false);
    if (isMobile && autoPlayOn) setAutoPlayOn(false);
  }, [isMobile, isFullscreen, autoPlayOn, setIsFullscreen, setAutoPlayOn]);

  // ✅ Guard: evita modal branco se não houver imagens
  if (!images || images.length === 0) {
    return (
      <div
        className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
        onClick={closeProject}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-lg font-black text-gray-900">Sem imagens</div>
          <p className="mt-2 text-sm text-gray-600">
            Este projeto não possui imagens cadastradas no momento.
          </p>
          <button
            type="button"
            className="mt-4 w-full py-3 rounded-xl bg-gray-100 hover:bg-gray-200 font-black"
            onClick={closeProject}
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

    // =========================
  // ✅ MOBILE UI — idêntico ao modelo (sem descrição)
  // =========================
  const safeIndex = Math.max(0, Math.min(images.length - 1, currentIndex));
  const currentMobile = images[safeIndex];

  const category =
    selectedProject?.categoryName ||
    selectedProject?.category_name ||
    "Projeto";

  const client = selectedProject?.client || selectedProject?.cliente || "—";
  const areaRaw = selectedProject?.area ?? "—";
  const year = selectedProject?.year ?? "—";

  const hasMany = images.length > 1;

  // ✅ formata área sem duplicar m² (aceita "700", "700m2", "700 m²", "700m²")
  const formatArea = (val) => {
    if (val == null) return "—";
    const s = String(val).trim();
    if (!s || s === "—") return "—";

    // remove "m2" / "m²" existentes, mantendo só o número/texto
    const clean = s.replace(/\s*m(?:²|2)\s*/gi, "").trim();
    if (!clean) return "—";
    return `${clean}m²`;
  };

  const goPrevMobile = () => {
    setCurrentIndex((i) => {
      if (images.length <= 1) return 0;
      return i <= 0 ? images.length - 1 : i - 1; // loop
    });
  };

  const goNextMobile = () => {
    setCurrentIndex((i) => {
      if (images.length <= 1) return 0;
      return i >= images.length - 1 ? 0 : i + 1; // loop
    });
  };

  // ✅ trava scroll do body apenas no mobile modal
  useEffect(() => {
    if (!isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile]);

  const mobileUI = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      onClick={closeProject}
    >
      {/* CARD */}
      <div
        className={[
          "w-full max-w-[560px]",
          "bg-white",
          "rounded-[34px]",
          "shadow-[0_30px_90px_rgba(0,0,0,0.45)]",
          "overflow-hidden",
        ].join(" ")}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start justify-between gap-4 min-w-0">
            <div className="min-w-0">
              {/* Category pill */}
              <div className="inline-flex items-center rounded-full bg-[#FF1616] text-white px-5 py-2 text-sm font-black shadow-sm">
                {category}
              </div>

              {/* Title (quebra suave: 2 linhas) */}
              <div className="mt-4 min-w-0">
                <h3 className="text-[28px] leading-[1.08] font-black text-gray-900 tracking-tight line-clamp-2">
                  {selectedProject?.title}
                </h3>
              </div>

              {/* Counter */}
              <div className="mt-2 text-[18px] font-semibold text-gray-500">
                {safeIndex + 1} / {Math.max(1, images.length)}
              </div>
            </div>

            {/* Close button (menor) */}
            <button
              type="button"
              onClick={closeProject}
              aria-label="Fechar"
              className={[
                "shrink-0",
                "w-12 h-12 rounded-full",
                "bg-gray-100/90 hover:bg-gray-200",
                "flex items-center justify-center",
                "text-gray-700",
                "shadow-[0_10px_22px_rgba(0,0,0,0.12)]",
                "active:scale-[0.98] transition",
              ].join(" ")}
            >
              <span className="text-[22px] leading-none">✕</span>
            </button>
          </div>
        </div>

        {/* IMAGE VIEWER */}
        <div className="px-6">
          <div className="relative rounded-[26px] overflow-hidden">
            {/* imagem */}
            <img
              src={currentMobile}
              alt={`${selectedProject?.title} ${safeIndex + 1}`}
              className="w-full h-[240px] object-cover"
              draggable={false}
              loading="lazy"
            />

            {/* ring leve como no print */}
            <div className="absolute inset-0 pointer-events-none ring-1 ring-black/10" />

            {/* arrows (menores + mais transparentes) */}
            {hasMany && (
              <>
                <button
                  type="button"
                  aria-label="Anterior"
                  onClick={(e) => {
                    e.stopPropagation();
                    goPrevMobile();
                  }}
                  className={[
                    "absolute left-4 top-1/2 -translate-y-1/2",
                    "w-11 h-11 rounded-full",
                    "bg-white/45 hover:bg-white/70",
                    "backdrop-blur-sm",
                    "shadow-[0_10px_22px_rgba(0,0,0,0.14)]",
                    "flex items-center justify-center",
                    "active:scale-[0.98] transition",
                  ].join(" ")}
                >
                  <span className="text-[26px] font-black text-gray-800 leading-none">
                    ‹
                  </span>
                </button>

                <button
                  type="button"
                  aria-label="Próxima"
                  onClick={(e) => {
                    e.stopPropagation();
                    goNextMobile();
                  }}
                  className={[
                    "absolute right-4 top-1/2 -translate-y-1/2",
                    "w-11 h-11 rounded-full",
                    "bg-white/45 hover:bg-white/70",
                    "backdrop-blur-sm",
                    "shadow-[0_10px_22px_rgba(0,0,0,0.14)]",
                    "flex items-center justify-center",
                    "active:scale-[0.98] transition",
                  ].join(" ")}
                >
                  <span className="text-[26px] font-black text-gray-800 leading-none">
                    ›
                  </span>
                </button>
              </>
            )}

            {/* badge "4/4" */}
            <div className="absolute bottom-4 left-4">
              <div className="rounded-full bg-black/70 text-white px-4 py-2 text-sm font-black shadow-lg">
                {safeIndex + 1}/{Math.max(1, images.length)}
              </div>
            </div>
          </div>

          {/* THUMBS (penumbra identica ao print: glow suave + sombra) */}
          {hasMany && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-2 [-webkit-overflow-scrolling:touch]">
              {images.map((img, i) => {
                const active = i === safeIndex;
                return (
                  <button
                    key={img + i}
                    type="button"
                    onClick={() => setCurrentIndex(i)}
                    className={[
                      "relative flex-shrink-0",
                      "w-[78px] h-[62px]",
                      "rounded-[16px] overflow-hidden",
                      "bg-black/5",
                      "ring-1 ring-black/10",
                      "shadow-[0_10px_18px_rgba(0,0,0,0.10)]",
                      active ? "ring-2 ring-[#FF1616]" : "",
                    ].join(" ")}
                    aria-label={`Abrir imagem ${i + 1}`}
                    title={`Imagem ${i + 1}`}
                  >
                    {/* ✅ penumbra vermelha igual ao print */}
                    {active && (
                      <span
                        className="absolute -inset-1 rounded-[18px] pointer-events-none"
                        style={{
                          boxShadow:
                            "0 0 0 6px rgba(255,22,22,0.22), 0 16px 34px rgba(255,22,22,0.28)",
                        }}
                      />
                    )}

                    <img
                      src={img}
                      alt={`Thumb ${i + 1}`}
                      className="w-full h-full object-cover"
                      draggable={false}
                      loading="lazy"
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* INFO (sem descrição) */}
        <div className="px-6 pt-6 pb-8">
          {/* Cliente (quebra suave: 2 linhas) */}
          <div className="flex items-start gap-4 min-w-0">
            <div className="mt-1 w-10 h-10 rounded-2xl bg-red-50 flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5 text-[#FF1616]" />
            </div>

            <div className="min-w-0">
              <div className="text-[18px] font-black text-gray-900">Cliente</div>
              <div className="mt-1 text-[18px] text-gray-500 font-medium line-clamp-2">
                {client}
              </div>
            </div>
          </div>

          {/* Área / Ano */}
          <div className="mt-6 grid grid-cols-2 gap-6">
            <div className="flex items-start gap-4 min-w-0">
              <div className="mt-1 w-10 h-10 rounded-2xl bg-red-50 flex items-center justify-center shrink-0">
                <Ruler className="w-5 h-5 text-[#FF1616]" />
              </div>

              <div className="min-w-0">
                <div className="text-[18px] font-black text-gray-900">Área</div>
                <div className="mt-1 text-[18px] text-gray-500 font-medium truncate">
                  {formatArea(areaRaw)}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4 justify-end min-w-0">
              <div className="mt-1 w-10 h-10 rounded-2xl bg-red-50 flex items-center justify-center shrink-0">
                <Calendar className="w-5 h-5 text-[#FF1616]" />
              </div>

              <div className="min-w-0 text-right">
                <div className="text-[18px] font-black text-gray-900">Ano</div>
                <div className="mt-1 text-[18px] text-gray-500 font-medium truncate">
                  {year}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // =========================
  // DESKTOP: seu modal premium original (mantido) + fullscreen Apple-like
  // =========================

  // ===== Slider physics refs =====
  const viewportRef = useRef(null);
  const trackRef = useRef(null);

  const rafRef = useRef(null);
  const isDraggingRef = useRef(false);

  const pointerIdRef = useRef(null);
  const startXRef = useRef(0);
  const lastXRef = useRef(0);
  const lastTRef = useRef(0);

  const [viewportW, setViewportW] = useState(1);
  const widthRef = useRef(1);

  const xRef = useRef(0);
  const vRef = useRef(0);

  const lastInteractRef = useRef(Date.now());

  const stopRAF = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const applyTransform = useCallback((x, withTransition) => {
    if (!trackRef.current) return;
    trackRef.current.style.transition = withTransition
      ? "transform 520ms cubic-bezier(0.22, 0.8, 0.2, 1)"
      : "none";
    trackRef.current.style.transform = `translate3d(${x}px,0,0)`;
  }, []);

  const clampIndex = useCallback(
    (i) => clamp(i, 0, Math.max(0, images.length - 1)),
    [images.length]
  );

  const startSpringToIndex = useCallback(
    (targetIndex) => {
      lastInteractRef.current = Date.now();
      stopRAF();

      const w = widthRef.current || 1;
      const targetX = -targetIndex * w;

      const stiffness = 0.1;
      const damping = 0.82;

      const step = () => {
        const x = xRef.current;
        let v = vRef.current;

        const force = (targetX - x) * stiffness;
        v = (v + force) * damping;

        const nextX = x + v;

        xRef.current = nextX;
        vRef.current = v;

        applyTransform(nextX, false);

        const done = Math.abs(targetX - nextX) < 0.5 && Math.abs(v) < 0.25;

        if (!done) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          xRef.current = targetX;
          vRef.current = 0;
          applyTransform(targetX, true);
          rafRef.current = null;
        }
      };

      rafRef.current = requestAnimationFrame(step);
    },
    [applyTransform, stopRAF]
  );

  // ===== measure width (desktop only) =====
  useEffect(() => {
    if (isMobile) return;

    const measure = () => {
      const w = viewportRef.current?.getBoundingClientRect()?.width || 1;
      const safeW = Math.max(1, Math.round(w));
      widthRef.current = safeW;
      setViewportW(safeW);

      xRef.current = -currentIndex * safeW;
      applyTransform(xRef.current, false);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [applyTransform, currentIndex, isMobile]);

  // lock body scroll (desktop only)
  useEffect(() => {
    if (isMobile) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile]);

  // sync when index changes (desktop only)
  useEffect(() => {
    if (isMobile) return;

    const w = widthRef.current || 1;
    xRef.current = -currentIndex * w;
    applyTransform(xRef.current, true);
  }, [applyTransform, currentIndex, isMobile]);

  // keyboard (desktop only)
  useEffect(() => {
    if (isMobile) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (isFullscreen) setIsFullscreen(false);
        else closeProject();
      }
      if (e.key === "ArrowLeft") {
        const next = clampIndex(currentIndex - 1);
        setCurrentIndex(next);
        startSpringToIndex(next);
      }
      if (e.key === "ArrowRight") {
        const next = clampIndex(currentIndex + 1);
        setCurrentIndex(next);
        startSpringToIndex(next);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    clampIndex,
    closeProject,
    currentIndex,
    isFullscreen,
    isMobile,
    setCurrentIndex,
    setIsFullscreen,
    startSpringToIndex,
  ]);

  // autoplay (desktop only)
  useEffect(() => {
    if (isMobile) return;
    if (!autoPlayOn) return;

    const interval = setInterval(() => {
      if (isFullscreen) return;
      if (isDraggingRef.current) return;

      const now = Date.now();
      const since = now - lastInteractRef.current;
      if (since < 2500) return;

      const next = clampIndex(currentIndex + 1);
      setCurrentIndex(next);
      startSpringToIndex(next);
    }, 3800);

    return () => clearInterval(interval);
  }, [
    autoPlayOn,
    clampIndex,
    currentIndex,
    isFullscreen,
    isMobile,
    setCurrentIndex,
    startSpringToIndex,
  ]);

  // ===== pointer swipe =====
  const onPointerDown = (e) => {
    if (isMobile) return;
    if (e.target.closest("[data-nav-button='1']")) return;
    if (!viewportRef.current || !trackRef.current) return;

    lastInteractRef.current = Date.now();
    stopRAF();

    pointerIdRef.current = e.pointerId;
    viewportRef.current.setPointerCapture(e.pointerId);

    isDraggingRef.current = true;

    startXRef.current = e.clientX;
    lastXRef.current = e.clientX;
    lastTRef.current = performance.now();

    applyTransform(xRef.current, false);
  };

  const onPointerMove = (e) => {
    if (isMobile) return;
    if (!isDraggingRef.current) return;
    if (pointerIdRef.current !== e.pointerId) return;

    const now = performance.now();
    const x = e.clientX;

    const dxTotal = x - startXRef.current;

    const dt = Math.max(8, now - lastTRef.current);
    const vx = (x - lastXRef.current) / dt;

    vRef.current = 0.86 * vRef.current + 0.14 * (vx * 16);

    lastXRef.current = x;
    lastTRef.current = now;

    const w = widthRef.current || 1;
    const baseX = -currentIndex * w;
    let nextX = baseX + dxTotal;

    const maxIndex = Math.max(0, images.length - 1);
    const minX = -maxIndex * w;
    const maxX = 0;

    if (nextX > maxX) {
      const over = nextX - maxX;
      nextX = maxX + rubberBand(over, w, 0.6);
    } else if (nextX < minX) {
      const over = nextX - minX;
      nextX = minX + rubberBand(over, w, 0.6);
    }

    xRef.current = nextX;
    applyTransform(nextX, false);
  };

  const onPointerUp = (e) => {
    if (isMobile) return;
    if (!isDraggingRef.current) return;
    if (pointerIdRef.current !== e.pointerId) return;

    lastInteractRef.current = Date.now();
    isDraggingRef.current = false;
    pointerIdRef.current = null;

    const w = widthRef.current || 1;
    const baseX = -currentIndex * w;
    const dx = xRef.current - baseX;

    const distThreshold = w * 0.16;
    const v = vRef.current;

    let next = currentIndex;
    if (dx < -distThreshold || v < -4.6) next = currentIndex + 1;
    else if (dx > distThreshold || v > 4.6) next = currentIndex - 1;

    next = clampIndex(next);

    setCurrentIndex(next);
    startSpringToIndex(next);
  };

  const onPointerCancel = () => {
    if (isMobile) return;
    isDraggingRef.current = false;
    pointerIdRef.current = null;
    startSpringToIndex(currentIndex);
  };

  const current = images[currentIndex];
  const thumbs = images;

  return isMobile ? (
    mobileUI
  ) : (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={closeProject}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/55" />

      <div
        className={[
          "relative w-full max-w-6xl",
          "rounded-[28px] bg-white",
          "shadow-[0_45px_140px_rgba(0,0,0,0.45)]",
          "ring-1 ring-black/10",
          "p-3",
        ].join(" ")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rounded-[22px] overflow-hidden bg-white">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px]">
            {/* LEFT TOP: viewer */}
            <div className="relative">
              <div className="relative h-[52vh] lg:h-[54vh] bg-black/5 overflow-hidden">
                <div
                  ref={viewportRef}
                  className="absolute inset-0 select-none touch-pan-y"
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerCancel}
                  style={{ touchAction: "pan-y" }}
                >
                  <div className="absolute inset-0">
                    <div
                      ref={trackRef}
                      className="h-full flex"
                      style={{
                        width: `${viewportW * Math.max(1, images.length)}px`,
                        transform: `translate3d(${
                          -currentIndex * viewportW
                        }px,0,0)`,
                        willChange: "transform",
                      }}
                    >
                      {images.map((img, i) => (
                        <div
                          key={i}
                          className="h-full flex-shrink-0"
                          style={{ width: `${viewportW}px` }}
                        >
                          <img
                            src={img}
                            alt={`${selectedProject.title} ${i + 1}`}
                            className="w-full h-full object-cover"
                            draggable={false}
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ✅ TELA CHEIA: somente desktop */}
                  <button
                    data-nav-button="1"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsFullscreen(true);
                    }}
                    className="hidden lg:inline-flex absolute top-3 left-3 rounded-full bg-white/90 hover:bg-white text-gray-900 font-black text-xs px-4 py-2 shadow-[0_10px_24px_rgba(0,0,0,0.18)] ring-1 ring-black/5"
                    title="Tela cheia"
                  >
                    TELA CHEIA
                  </button>

                  <button
                    data-nav-button="1"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      closeProject();
                    }}
                    className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-gray-900 font-black shadow-[0_10px_24px_rgba(0,0,0,0.18)] ring-1 ring-black/5 flex items-center justify-center"
                    aria-label="Fechar"
                    title="Fechar"
                  >
                    ✕
                  </button>

                  <button
                    data-nav-button="1"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const next = clampIndex(currentIndex - 1);
                      setCurrentIndex(next);
                      startSpringToIndex(next);
                    }}
                    disabled={currentIndex === 0}
                    className={[
                      "absolute left-3 top-1/2 -translate-y-1/2",
                      "w-9 h-9 rounded-full",
                      "bg-white/90 hover:bg-white",
                      "shadow-[0_10px_24px_rgba(0,0,0,0.18)] ring-1 ring-black/5",
                      "flex items-center justify-center font-black",
                      currentIndex === 0 ? "opacity-40 cursor-not-allowed" : "",
                    ].join(" ")}
                    aria-label="Anterior"
                    title="Anterior"
                  >
                    ‹
                  </button>

                  <button
                    data-nav-button="1"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const next = clampIndex(currentIndex + 1);
                      setCurrentIndex(next);
                      startSpringToIndex(next);
                    }}
                    disabled={currentIndex === images.length - 1}
                    className={[
                      "absolute right-3 top-1/2 -translate-y-1/2",
                      "w-9 h-9 rounded-full",
                      "bg-white/90 hover:bg-white",
                      "shadow-[0_10px_24px_rgba(0,0,0,0.18)] ring-1 ring-black/5",
                      "flex items-center justify-center font-black",
                      currentIndex === images.length - 1
                        ? "opacity-40 cursor-not-allowed"
                        : "",
                    ].join(" ")}
                    aria-label="Próximo"
                    title="Próximo"
                  >
                    ›
                  </button>

                  <div className="absolute bottom-3 left-3 bg-black/70 text-white text-xs font-black px-3 py-1.5 rounded-full">
                    {currentIndex + 1}/{images.length}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: THUMBS (desktop) */}
            <aside className="bg-white border-l border-gray-200 lg:row-span-2">
              <div className="p-3 h-full">
                <div className="h-full overflow-auto pr-1">
                  <div className="flex flex-col gap-3">
                    {thumbs.map((img, i) => {
                      const active = i === currentIndex;
                      return (
                        <button
                          key={img + i}
                          data-nav-button="1"
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.currentTarget.blur();
                            setCurrentIndex(i);
                            startSpringToIndex(i);
                          }}
                          className={[
                            "w-full rounded-2xl overflow-hidden",
                            "ring-1 ring-inset",
                            active ? "ring-2 ring-[#FF1616]" : "ring-black/10",
                            "shadow-[0_12px_26px_rgba(0,0,0,0.10)]",
                            "bg-black/5",
                            "outline-none focus:outline-none",
                            "transition-transform active:scale-[0.99]",
                          ].join(" ")}
                          title={`Imagem ${i + 1}`}
                        >
                          <div className="relative w-full aspect-[16/9]">
                            <img
                              src={img}
                              alt={`${selectedProject.title} thumb ${i + 1}`}
                              className="absolute inset-0 w-full h-full object-cover select-none"
                              draggable={false}
                              loading="lazy"
                            />
                          </div>
                        </button>
                      );
                    })}

                    <Button
                      data-nav-button="1"
                      type="button"
                      variant={autoPlayOn ? "default" : "outline"}
                      className={
                        autoPlayOn
                          ? "bg-[#FF1616] hover:bg-red-700 text-white font-black"
                          : "border-gray-200 hover:bg-gray-50 font-black"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setAutoPlayOn((v) => !v);
                      }}
                      title="Auto-play"
                    >
                      {autoPlayOn ? "AUTO: ON" : "AUTO: OFF"}
                    </Button>
                  </div>
                </div>
              </div>
            </aside>

            {/* LEFT BOTTOM: info */}
            <div className="bg-white border-t border-gray-200 p-6">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
                  {selectedProject.title}
                </h3>

                <Badge className="bg-[#FF1616] text-white hover:bg-red-700 font-black tracking-wide px-4 py-2 rounded-full">
                  {selectedProject.categoryName || selectedProject.category_name}
                </Badge>
              </div>

              <div className="mt-3 text-gray-600">
                <TruncatedText lines={3} className="text-base leading-relaxed">
                  {selectedProject.description}
                </TruncatedText>
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <Ruler className="w-5 h-5 text-[#FF1616]" />
                  </div>
                  <div>
                    <div className="font-black text-gray-900">Área</div>
                    <div className="text-gray-600">
                      {selectedProject.area || "-"}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <Calendar className="w-5 h-5 text-[#FF1616]" />
                  </div>
                  <div>
                    <div className="font-black text-gray-900">Ano</div>
                    <div className="text-gray-600">
                      {selectedProject.year || "-"}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <MapPin className="w-5 h-5 text-[#FF1616]" />
                  </div>
                  <div>
                    <div className="font-black text-gray-900">Cliente</div>
                    <div className="text-gray-600">
                      {selectedProject.client || "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FULLSCREEN (desktop) */}
          {isFullscreen && (
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-in fade-in duration-200"
              onClick={() => setIsFullscreen(false)}
              role="dialog"
              aria-modal="true"
            >
              <button
                data-nav-button="1"
                type="button"
                aria-label="Fechar galeria"
                className="absolute top-4 right-4 z-[70] p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors text-white font-black"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFullscreen(false);
                }}
              >
                ✕
              </button>

              {images.length > 1 && (
                <button
                  data-nav-button="1"
                  type="button"
                  aria-label="Imagem anterior"
                  className="absolute left-4 z-[70] p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors text-white font-black"
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = clampIndex(currentIndex - 1);
                    setCurrentIndex(next);
                    startSpringToIndex(next);
                  }}
                  disabled={currentIndex === 0}
                  style={{ opacity: currentIndex === 0 ? 0.35 : 1 }}
                >
                  ‹
                </button>
              )}

              {images.length > 1 && (
                <button
                  data-nav-button="1"
                  type="button"
                  aria-label="Próxima imagem"
                  className="absolute right-4 z-[70] p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors text-white font-black"
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = clampIndex(currentIndex + 1);
                    setCurrentIndex(next);
                    startSpringToIndex(next);
                  }}
                  disabled={currentIndex === images.length - 1}
                  style={{
                    opacity: currentIndex === images.length - 1 ? 0.35 : 1,
                  }}
                >
                  ›
                </button>
              )}

              <div
                className="relative max-w-7xl max-h-[90vh] w-full mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={current}
                  alt={`Imagem ${currentIndex + 1} de ${images.length}`}
                  className="w-full h-full max-h-[90vh] object-contain rounded-lg select-none"
                  draggable={false}
                />

                {images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    {currentIndex + 1} / {images.length}
                  </div>
                )}

                {images.length > 1 && (
                  <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 flex gap-2 bg-black/70 p-2 rounded-lg">
                    {thumbs.map((img, i) => {
                      const active = i === currentIndex;
                      return (
                        <button
                          key={img + i}
                          data-nav-button="1"
                          type="button"
                          className={[
                            "w-16 h-16 rounded overflow-hidden transition-all",
                            active
                              ? "opacity-100 ring-2 ring-white/80"
                              : "opacity-50 hover:opacity-75",
                          ].join(" ")}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentIndex(i);
                            startSpringToIndex(i);
                          }}
                          aria-label={`Miniatura ${i + 1}`}
                          title={`Imagem ${i + 1}`}
                        >
                          <img
                            src={img}
                            alt={`Miniatura ${i + 1}`}
                            className="w-full h-full object-cover select-none"
                            draggable={false}
                            loading="lazy"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="absolute bottom-4 left-4 text-white/70 text-sm">
                <p>← → para navegar | ESC para fechar</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Portfolio;
